import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Sparkles, Hash, FileText, Tag, PartyPopper, MessageSquare,
  Loader2, Clock, Shield, SlidersHorizontal, AlertTriangle, Megaphone,
  Library, CheckCircle2, XCircle, Edit3, Copy, RefreshCw, Plus, Workflow,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AutomationSection, AddToCalendarButton } from "@/components/aina/AutomationSection";

/* --------------------------------------------------------- shared */
const KINDS = [
  { id: "post",                   label: "Social Post",     icon: MessageSquare, color: "from-fuchsia-600 to-rose-600" },
  { id: "caption",                label: "Caption",         icon: Sparkles,      color: "from-sky-600 to-cyan-500"   },
  { id: "hashtags",               label: "Hashtags",        icon: Hash,          color: "from-emerald-600 to-teal-500" },
  { id: "product-description",   label: "Product Desc",   icon: FileText,      color: "from-indigo-600 to-violet-600" },
  { id: "offer",                  label: "Offer",           icon: Tag,           color: "from-orange-600 to-amber-500" },
  { id: "festival-campaign",     label: "Festival Camp.", icon: PartyPopper,   color: "from-pink-600 to-rose-500" },
];

const PLATFORMS = ["instagram", "facebook", "whatsapp", "twitter", "linkedin", "generic"];
const CAMPAIGN_PLATFORMS = ["find2near", "facebook", "instagram", "whatsapp", "google_business"];
const SECTIONS = [
  { id: "studio",    label: "Studio",    icon: Sparkles },
  { id: "campaigns", label: "Campaigns", icon: Megaphone },
  { id: "library",   label: "Library",   icon: Library },
  { id: "automation", label: "Automation", icon: Workflow },
];

/* --------------------------------------------------------- main */
export default function AinaStudio() {
  const nav = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [bizId, setBizId] = useState(null);
  const [memory, setMemory] = useState(null);
  const [quota, setQuota] = useState(null);
  const [section, setSection] = useState("studio");
  const [tab, setTab] = useState("post");
  const [history, setHistory] = useState([]);
  const [unauth, setUnauth] = useState(false);

  const loadBusinesses = useCallback(async () => {
    try {
      const r = await api.get("/business/mine");
      const d = r.data;
      const items = Array.isArray(d) ? d : (d?.businesses || d?.items || []);
      setBusinesses(items);
      if (items.length && !bizId) setBizId(items[0].business_id);
    } catch (e) {
      if (e?.response?.status === 401) { setUnauth(true); return; }
    }
  }, [bizId]);

  const loadBiz = useCallback(async () => {
    if (!bizId) return;
    try {
      const [m, q, h] = await Promise.all([
        api.get(`/aina/memory/${bizId}`),
        api.get(`/aina/quota/${bizId}`),
        api.get(`/aina/studio/history?business_id=${bizId}`),
      ]);
      setMemory(m.data.memory);
      setQuota(q.data);
      setHistory(h.data.items || []);
    } catch { /* ignored */ }
  }, [bizId]);

  useEffect(() => { loadBusinesses(); }, [loadBusinesses]);
  useEffect(() => { loadBiz(); }, [loadBiz]);

  if (unauth) {
    return (
      <div className="min-h-screen bg-stone-50 grid place-items-center p-6">
        <div className="bg-white border border-stone-200 rounded-2xl p-4 text-center" data-testid="aina-need-login">
          <div className="font-black">Sign in to use AINA</div>
          <button onClick={() => nav("/")} className="mt-3 px-4 py-2 rounded-full bg-indigo-600 text-white font-black text-xs">Go to sign in</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="aina-studio-root">
      <div className="sticky top-0 z-10 bg-white border-b border-stone-200">
        <div className="max-w-5xl mx-auto flex items-center gap-2 px-3 py-2">
          <button onClick={() => nav(-1)} className="p-1.5 rounded-full hover:bg-stone-100" data-testid="aina-back"><ArrowLeft size={18}/></button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase font-black tracking-widest text-fuchsia-700">AINA · Business OS</div>
            <div className="font-black text-sm truncate">Marketing Studio</div>
          </div>
          <select value={bizId || ""} onChange={(e) => setBizId(e.target.value)}
                    data-testid="aina-biz-picker"
                    className="text-[11px] font-black px-2 py-1 rounded-lg border border-stone-200 bg-white">
            {businesses.length === 0 && <option value="">No business</option>}
            {businesses.map((b) => <option key={b.business_id} value={b.business_id}>{b.name || b.business_id}</option>)}
          </select>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-3 space-y-3">
        {!bizId && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 text-[12px]" data-testid="aina-no-biz">
            You need a business profile first. Visit <b>Business 360 · Profile</b> to create one.
          </div>
        )}

        {bizId && (
          <>
            {/* memory + quota strip */}
            <div className="grid grid-cols-2 gap-2">
              <QuotaCard quota={quota}/>
              <MemoryCard memory={memory}
                            onEdit={async () => {
                              const nd = window.prompt("One-line description for this business (used to ground AI content):",
                                                        memory?.description || "");
                              if (nd === null) return;
                              const cat = window.prompt("Category (e.g. Jewellery, Cafe):", memory?.category || "");
                              const tone = window.prompt("Brand tone (e.g. warm, premium):", memory?.brand_tone || "");
                              await api.put(`/aina/memory/${bizId}`, {
                                description: nd || null,
                                category: cat || null,
                                brand_tone: tone || null,
                              });
                              toast.success("Business memory saved");
                              loadBiz();
                            }}/>
            </div>
            {!memory?.exists && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-2 text-[11px] text-amber-800 flex gap-2"
                   data-testid="aina-memory-warning">
                <AlertTriangle size={14} className="shrink-0"/>
                <div>AI will NOT invent facts. Fill business memory first for grounded output.</div>
              </div>
            )}

            {/* section switcher */}
            <div className="flex gap-1 flex-wrap">
              {SECTIONS.map((s) => {
                const Ic = s.icon;
                return (
                  <button key={s.id} onClick={() => setSection(s.id)}
                          data-testid={`aina-section-${s.id}`}
                          className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-black uppercase ${
                            section === s.id ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"}`}>
                    <Ic size={12}/> {s.label}
                  </button>
                );
              })}
            </div>

            {section === "studio" && (
              <>
                {/* kind picker */}
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  {KINDS.map((k) => {
                    const Ic = k.icon;
                    return (
                      <button key={k.id} onClick={() => setTab(k.id)}
                              data-testid={`aina-kind-${k.id}`}
                              className={`rounded-xl p-2 text-white text-left bg-gradient-to-br ${k.color} ${tab === k.id ? "ring-2 ring-stone-900" : "opacity-80 hover:opacity-100"}`}>
                        <Ic size={16}/>
                        <div className="font-black text-[11px] mt-1 leading-tight">{k.label}</div>
                      </button>
                    );
                  })}
                </div>
                {/* kind form */}
                <KindForm key={tab} kind={tab} bizId={bizId} onDone={loadBiz}/>
                {/* history */}
                <HistoryList items={history}/>
              </>
            )}

            {section === "campaigns" && <CampaignsSection bizId={bizId} memory={memory} onDone={loadBiz}/>}
            {section === "library"   && <LibrarySection bizId={bizId}/>}
            {section === "automation" && <AutomationSection bizId={bizId}/>}
          </>
        )}
      </div>
    </div>
  );
}

/* --------------------------------------------------------- quota card */
function QuotaCard({ quota }) {
  if (!quota) return <SkeletonCard/>;
  const t = quota.config.limits.text;
  const u = quota.usage.usage.text;
  const pct = Math.min(100, Math.round((u / Math.max(1, t)) * 100));
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-2" data-testid="aina-quota-card">
      <div className="flex items-center gap-1 text-[9px] uppercase font-black text-stone-500">
        <SlidersHorizontal size={10}/> Text quota · {quota.usage.period}
      </div>
      <div className="font-black text-sm">{u} / {t}</div>
      <div className="mt-1 h-1 rounded-full bg-stone-100">
        <div className={`h-1 rounded-full ${pct > 85 ? "bg-rose-500" : "bg-indigo-500"}`} style={{ width: `${pct}%` }}/>
      </div>
    </div>
  );
}

function MemoryCard({ memory, onEdit }) {
  if (!memory) return <SkeletonCard/>;
  return (
    <button onClick={onEdit} data-testid="aina-memory-card"
            className="text-left bg-white border border-stone-200 rounded-xl p-2 hover:border-fuchsia-300">
      <div className="flex items-center gap-1 text-[9px] uppercase font-black text-stone-500">
        <Shield size={10}/> Business memory
      </div>
      <div className="font-black text-[12px] truncate">{memory.description || "Tap to add"}</div>
      <div className="text-[10px] text-stone-500 truncate">
        {memory.category || "No category"} · {memory.brand_tone || "no tone"}
      </div>
    </button>
  );
}

function SkeletonCard() {
  return <div className="bg-white border border-stone-200 rounded-xl p-2 h-14 animate-pulse"/>;
}

/* --------------------------------------------------------- kind form */
function KindForm({ kind, bizId, onDone }) {
  const [input, setInput] = useState({});
  const [platform, setPlatform] = useState("instagram");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const fields = FIELD_MAP[kind] || [];

  const run = async () => {
    const missing = fields.filter((f) => f.required && !input[f.key]);
    if (missing.length) {
      toast.error(`Fill: ${missing.map((m) => m.label).join(", ")}`);
      return;
    }
    const payload = { business_id: bizId, platform, ...input };
    // coerce numeric fields
    if (payload.count) payload.count = Number(payload.count);
    // coerce comma lists
    if (typeof payload.key_features === "string") {
      payload.key_features = payload.key_features.split(",").map((s) => s.trim()).filter(Boolean);
    }
    if (typeof payload.hero_items === "string") {
      payload.hero_items = payload.hero_items.split(",").map((s) => s.trim()).filter(Boolean);
    }
    setBusy(true);
    try {
      const r = await api.post(`/aina/studio/${kind}`, payload);
      setDraft(r.data.draft);
      onDone && onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (typeof d === "object" && d?.kind) {
        toast.error(`Quota: ${d.used}/${d.limit} used for ${d.kind} this ${d.period}`);
      } else {
        toast.error(d || "AI call failed");
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-3 space-y-2" data-testid={`aina-form-${kind}`}>
      <div className="flex items-center gap-2">
        <select value={platform} onChange={(e) => setPlatform(e.target.value)}
                  data-testid="aina-platform"
                  className="text-[11px] font-black px-2 py-1 rounded-lg border border-stone-200">
          {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <div className="text-[10px] text-stone-500">All drafts are save-only until you approve + publish.</div>
      </div>
      {fields.map((f) => (
        <label key={f.key} className="block">
          <span className="text-[9px] uppercase font-black text-stone-500">{f.label}{f.required ? " *" : ""}</span>
          {f.multiline ? (
            <textarea rows={3} value={input[f.key] || ""} onChange={(e) => setInput({ ...input, [f.key]: e.target.value })}
                      data-testid={`aina-f-${f.key}`}
                      placeholder={f.hint}
                      className="w-full px-2 py-1.5 rounded-lg border border-stone-200 text-sm"/>
          ) : (
            <input value={input[f.key] || ""} onChange={(e) => setInput({ ...input, [f.key]: e.target.value })}
                   data-testid={`aina-f-${f.key}`}
                   placeholder={f.hint}
                   className="w-full px-2 py-1.5 rounded-lg border border-stone-200 text-sm"/>
          )}
        </label>
      ))}
      <button onClick={run} disabled={busy}
              data-testid={`aina-run-${kind}`}
              className="w-full py-2 rounded-full bg-stone-900 text-white font-black text-xs disabled:opacity-50">
        {busy ? <Loader2 size={12} className="animate-spin inline mr-1"/> : <Sparkles size={12} className="inline mr-1"/>}
        Generate draft
      </button>
      {draft && (
        <div className="mt-2 bg-stone-50 rounded-xl p-3" data-testid="aina-draft-output">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase font-black text-stone-500">Draft · {draft.kind}</div>
            {draft.fallback && (
              <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-amber-500 text-white">FALLBACK</span>
            )}
          </div>
          <pre className="mt-1 text-[12px] whitespace-pre-wrap font-sans text-stone-800">{draft.output}</pre>
          <button onClick={async () => {
            try { await navigator.clipboard.writeText(draft.output); toast.success("Copied"); } catch { /* ignored */ }
          }} className="mt-2 text-[10px] font-black text-indigo-600">Copy draft</button>
        </div>
      )}
    </div>
  );
}

const FIELD_MAP = {
  "post":                 [{ key: "topic",              label: "Topic",             required: true, hint: "e.g. Diwali Grand Sale" }],
  "caption":              [{ key: "content",            label: "Content",           required: true, multiline: true, hint: "What is this post about?" }],
  "hashtags":             [{ key: "content",            label: "Content",           required: true, multiline: true },
                           { key: "count",              label: "Count",             hint: "default 10" }],
  "product-description": [{ key: "product_name",       label: "Product name",      required: true },
                           { key: "key_features",       label: "Key features (comma-sep)", hint: "handcrafted, 22kt" }],
  "offer":                [{ key: "product_or_service", label: "Product/service",  required: true },
                           { key: "occasion",            label: "Occasion",          hint: "e.g. Diwali" }],
  "festival-campaign":   [{ key: "festival",           label: "Festival",          required: true, hint: "Diwali, Eid, etc." },
                           { key: "hero_items",          label: "Hero items (comma-sep)" }],
};

/* --------------------------------------------------------- history */
function HistoryList({ items }) {
  if (!items.length) return (
    <div className="p-4 text-center text-stone-500 text-xs italic bg-white border border-stone-200 rounded-xl">
      No drafts yet.
    </div>
  );
  return (
    <div className="space-y-2" data-testid="aina-history">
      <div className="text-[10px] uppercase font-black text-stone-500 flex items-center gap-1">
        <Clock size={10}/> Recent drafts
      </div>
      {items.map((it) => (
        <div key={it.generation_id} className="bg-white border border-stone-200 rounded-xl p-2"
             data-testid={`aina-hist-${it.generation_id}`}>
          <div className="flex items-center justify-between">
            <div className="font-black text-[12px]">{it.kind}</div>
            <div className="text-[10px] text-stone-500">{new Date(it.created_at).toLocaleString()}</div>
          </div>
          <div className="text-[11px] text-stone-500 truncate">{it.input}</div>
          <pre className="mt-1 text-[11px] whitespace-pre-wrap font-sans text-stone-800 max-h-24 overflow-auto">{it.output}</pre>
        </div>
      ))}
    </div>
  );
}

/* --------------------------------------------------------- Campaigns */
function CampaignsSection({ bizId, memory, onDone }) {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [brief, setBrief] = useState({
    name: "", objective: "awareness", occasion: "",
    platforms: CAMPAIGN_PLATFORMS, languages: ["en"], tone: "", audience: "", notes: "",
  });

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/aina/campaigns?business_id=${bizId}`);
      setItems(r.data.items || []);
    } catch { /* ignored */ }
  }, [bizId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/aina/templates/festivals").then((r) => setTemplates(r.data.items || [])).catch(() => {});
  }, []);

  const openCampaign = async (cid) => {
    try {
      const r = await api.get(`/aina/campaigns/${cid}`);
      setActive(r.data);
    } catch (e) { toast.error("Failed to load campaign"); }
  };

  const createCampaign = async () => {
    if (!brief.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/aina/campaigns", {
        business_id: bizId,
        name: brief.name.trim(),
        objective: brief.objective,
        occasion: brief.occasion || null,
        platforms: brief.platforms,
        languages: brief.languages,
        tone: brief.tone || null,
        audience: brief.audience || null,
        notes: brief.notes || null,
      });
      toast.success("Campaign created");
      setShowCreate(false);
      load();
      openCampaign(r.data.campaign.campaign_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const generate = async (cid) => {
    setBusy(true);
    try {
      const r = await api.post(`/aina/campaigns/${cid}/generate`);
      toast.success(`Generated ${r.data.drafts.length} variants (v${r.data.version})`);
      openCampaign(cid);
      onDone && onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (typeof d === "object" && d?.kind) {
        toast.error(`Quota: ${d.used}/${d.limit} used for ${d.kind}`);
      } else { toast.error(d || "Generate failed"); }
    } finally { setBusy(false); }
  };

  const toggleLang = (lang) => {
    setBrief((b) => ({ ...b, languages: b.languages.includes(lang)
                                    ? b.languages.filter((l) => l !== lang)
                                    : [...b.languages, lang] }));
  };
  const togglePlatform = (p) => {
    setBrief((b) => ({ ...b, platforms: b.platforms.includes(p)
                                    ? b.platforms.filter((x) => x !== p)
                                    : [...b.platforms, p] }));
  };

  if (active) {
    return <CampaignDetail detail={active} onClose={() => { setActive(null); load(); }}
                             onRegenerate={() => generate(active.campaign.campaign_id)}
                             busy={busy}/>;
  }

  return (
    <div className="space-y-2" data-testid="aina-campaigns">
      <button onClick={() => setShowCreate((v) => !v)} data-testid="aina-campaign-new"
              className="w-full py-2 rounded-full bg-fuchsia-600 text-white font-black text-xs">
        <Plus size={12} className="inline mr-1"/> New campaign brief
      </button>

      {showCreate && (
        <div className="bg-white border border-stone-200 rounded-2xl p-3 space-y-2" data-testid="aina-campaign-create-form">
          <input value={brief.name} onChange={(e) => setBrief({ ...brief, name: e.target.value })}
                 placeholder="Campaign name (eg Diwali Mega Sale)"
                 data-testid="aina-brief-name"
                 className="w-full px-2 py-1.5 rounded-lg border border-stone-200 text-sm"/>
          <div className="grid grid-cols-2 gap-2">
            <select value={brief.objective} onChange={(e) => setBrief({ ...brief, objective: e.target.value })}
                      data-testid="aina-brief-objective"
                      className="px-2 py-1.5 rounded-lg border border-stone-200 text-sm">
              {["awareness", "leads", "orders", "store_visit", "message", "website"].map((o) => (
                <option key={o} value={o}>{o.replace("_", " ")}</option>
              ))}
            </select>
            <select value={brief.occasion} onChange={(e) => setBrief({ ...brief, occasion: e.target.value })}
                      data-testid="aina-brief-occasion"
                      className="px-2 py-1.5 rounded-lg border border-stone-200 text-sm">
              <option value="">— No occasion —</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[9px] uppercase font-black text-stone-500 mb-1">Platforms</div>
            <div className="flex flex-wrap gap-1">
              {CAMPAIGN_PLATFORMS.map((p) => (
                <button key={p} onClick={() => togglePlatform(p)}
                        data-testid={`aina-brief-platform-${p}`}
                        className={`text-[10px] uppercase font-black px-2 py-1 rounded-full ${
                          brief.platforms.includes(p) ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase font-black text-stone-500 mb-1">Languages</div>
            <div className="flex flex-wrap gap-1">
              {(memory?.languages || ["en", "hi"]).map((l) => (
                <button key={l} onClick={() => toggleLang(l)}
                        className={`text-[10px] uppercase font-black px-2 py-1 rounded-full ${
                          brief.languages.includes(l) ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <input value={brief.tone} onChange={(e) => setBrief({ ...brief, tone: e.target.value })}
                 placeholder="Tone (optional, overrides brand tone)"
                 className="w-full px-2 py-1.5 rounded-lg border border-stone-200 text-sm"/>
          <input value={brief.audience} onChange={(e) => setBrief({ ...brief, audience: e.target.value })}
                 placeholder="Audience (optional)"
                 className="w-full px-2 py-1.5 rounded-lg border border-stone-200 text-sm"/>
          <textarea rows={2} value={brief.notes} onChange={(e) => setBrief({ ...brief, notes: e.target.value })}
                    placeholder="Extra notes (optional)"
                    className="w-full px-2 py-1.5 rounded-lg border border-stone-200 text-sm"/>
          <button onClick={createCampaign} disabled={busy} data-testid="aina-brief-create"
                  className="w-full py-2 rounded-full bg-stone-900 text-white font-black text-xs disabled:opacity-50">
            {busy ? <Loader2 size={12} className="animate-spin inline mr-1"/> : null}
            Create & open
          </button>
        </div>
      )}

      {items.length === 0 && !showCreate && (
        <div className="p-4 text-center text-stone-500 text-xs italic bg-white border border-stone-200 rounded-xl">
          No campaigns yet. Click "New campaign brief" to create one.
        </div>
      )}
      {items.map((cmp) => (
        <button key={cmp.campaign_id} onClick={() => openCampaign(cmp.campaign_id)}
                data-testid={`aina-campaign-${cmp.campaign_id}`}
                className="w-full text-left bg-white border border-stone-200 rounded-xl p-2">
          <div className="flex items-center justify-between">
            <div className="font-black text-sm truncate">{cmp.name}</div>
            <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-fuchsia-600 text-white uppercase">
              v{cmp.last_generated_version || 0}
            </span>
          </div>
          <div className="text-[10px] text-stone-500">
            {cmp.objective} · {cmp.occasion || "—"} · {cmp.platforms?.length || 0} platforms · {cmp.languages?.length || 0} langs
          </div>
        </button>
      ))}
    </div>
  );
}

function CampaignDetail({ detail, onClose, onRegenerate, busy }) {
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState("");
  const [drafts, setDrafts] = useState(detail.drafts);

  const refresh = async () => {
    try {
      const r = await api.get(`/aina/campaigns/${detail.campaign.campaign_id}`);
      setDrafts(r.data.drafts);
    } catch { /* ignored */ }
  };

  const act = async (did, action) => {
    try {
      await api.post(`/aina/drafts/${did}/${action}`);
      toast.success(action === "approve" ? "Approved" : "Rejected");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveEdit = async () => {
    try {
      await api.post(`/aina/drafts/${editing}/edit`, { content: editText });
      toast.success("Edited (new version)");
      setEditing(null); setEditText("");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { /* ignored */ }
  };

  const cmp = detail.campaign;
  const latest = Math.max(...drafts.map((d) => d.version || 1), 0);
  const grouped = {};
  for (const d of drafts) {
    const k = `${d.platform}::${d.language}`;
    if (!grouped[k] || (d.version || 1) > (grouped[k].version || 1)) grouped[k] = d;
  }
  const latestOnly = Object.values(grouped);

  return (
    <div className="space-y-2" data-testid="aina-campaign-detail">
      <div className="bg-white border border-stone-200 rounded-2xl p-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase font-black text-fuchsia-700">Campaign</div>
            <div className="font-black text-sm">{cmp.name}</div>
            <div className="text-[10px] text-stone-500">
              {cmp.objective} · {cmp.occasion || "—"} · v{latest}
            </div>
          </div>
          <button onClick={onClose} data-testid="aina-campaign-back"
                  className="text-xs text-stone-500 font-black">← Back</button>
        </div>
        <button onClick={onRegenerate} disabled={busy} data-testid="aina-campaign-regenerate"
                className="mt-2 w-full py-2 rounded-full bg-fuchsia-600 text-white font-black text-xs disabled:opacity-50">
          {busy ? <Loader2 size={12} className="animate-spin inline mr-1"/> : <RefreshCw size={12} className="inline mr-1"/>}
          {latest === 0 ? "Generate drafts" : `Regenerate (→ v${latest + 1})`}
        </button>
      </div>

      {latestOnly.length === 0 && (
        <div className="p-4 text-center text-stone-500 text-xs italic bg-white border border-stone-200 rounded-xl">
          No drafts yet. Click "Generate drafts" above.
        </div>
      )}
      {latestOnly.map((d) => {
        const Ic = d.approval_status === "approved" ? CheckCircle2
                 : d.approval_status === "rejected" ? XCircle : Edit3;
        const badge = d.approval_status === "approved" ? "bg-emerald-600"
                     : d.approval_status === "rejected" ? "bg-rose-600"
                     : "bg-stone-500";
        const isEditing = editing === d.draft_id;
        return (
          <div key={d.draft_id} className="bg-white border border-stone-200 rounded-2xl p-3"
               data-testid={`aina-draft-${d.draft_id}`}>
            <div className="flex items-center justify-between mb-1">
              <div>
                <div className="text-[10px] uppercase font-black text-stone-500">{d.platform} · {d.language} · v{d.version}</div>
                {d.fallback && <span className="text-[9px] font-black text-amber-700">FALLBACK</span>}
              </div>
              <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full text-white uppercase ${badge}`}>
                <Ic size={9} className="inline"/> {d.approval_status}
              </span>
            </div>
            {isEditing ? (
              <>
                <textarea rows={6} value={editText} onChange={(e) => setEditText(e.target.value)}
                          data-testid={`aina-edit-${d.draft_id}`}
                          className="w-full px-2 py-1.5 rounded-lg border border-stone-200 text-[12px]"/>
                <div className="flex gap-1 mt-1">
                  <button onClick={saveEdit} className="flex-1 py-1 rounded-full bg-stone-900 text-white font-black text-[10px] uppercase">Save edit</button>
                  <button onClick={() => setEditing(null)} className="py-1 px-2 rounded-full bg-stone-100 text-stone-600 font-black text-[10px] uppercase">Cancel</button>
                </div>
              </>
            ) : (
              <>
                <pre className="text-[11.5px] whitespace-pre-wrap font-sans text-stone-800">{d.content}</pre>
                <div className="flex flex-wrap gap-1 mt-2">
                  <button onClick={() => act(d.draft_id, "approve")} data-testid={`aina-approve-${d.draft_id}`}
                          className="text-[10px] font-black px-2 py-1 rounded-full bg-emerald-600 text-white uppercase">Approve</button>
                  <button onClick={() => act(d.draft_id, "reject")} data-testid={`aina-reject-${d.draft_id}`}
                          className="text-[10px] font-black px-2 py-1 rounded-full bg-rose-600 text-white uppercase">Reject</button>
                  <button onClick={() => { setEditing(d.draft_id); setEditText(d.content); }}
                          data-testid={`aina-edit-btn-${d.draft_id}`}
                          className="text-[10px] font-black px-2 py-1 rounded-full bg-stone-900 text-white uppercase">
                    <Edit3 size={9} className="inline"/> Edit
                  </button>
                  {d.approval_status !== "rejected" && <AddToCalendarButton bizId={detail.campaign.business_id} draft={d}/>}
                  <button onClick={() => copy(d.content)} data-testid={`aina-copy-${d.draft_id}`}
                          className="text-[10px] font-black px-2 py-1 rounded-full bg-stone-100 text-stone-700 uppercase">
                    <Copy size={9} className="inline"/> Copy
                  </button>
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* --------------------------------------------------------- Library */
function LibrarySection({ bizId }) {
  const [items, setItems] = useState([]);
  const [platform, setPlatform] = useState("");
  const [status, setStatus] = useState("");
  const load = useCallback(async () => {
    try {
      const q = new URLSearchParams({ business_id: bizId });
      if (platform) q.set("platform", platform);
      if (status) q.set("approval_status", status);
      const r = await api.get(`/aina/drafts/library?${q.toString()}`);
      setItems(r.data.items || []);
    } catch { /* ignored */ }
  }, [bizId, platform, status]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-2" data-testid="aina-library">
      <div className="flex flex-wrap gap-1">
        <select value={platform} onChange={(e) => setPlatform(e.target.value)}
                  data-testid="aina-lib-platform"
                  className="text-[11px] font-black px-2 py-1 rounded-lg border border-stone-200">
          <option value="">All platforms</option>
          {CAMPAIGN_PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
                  data-testid="aina-lib-status"
                  className="text-[11px] font-black px-2 py-1 rounded-lg border border-stone-200">
          <option value="">All statuses</option>
          <option value="draft">draft</option>
          <option value="approved">approved</option>
          <option value="rejected">rejected</option>
        </select>
      </div>
      {items.length === 0 && (
        <div className="p-4 text-center text-stone-500 text-xs italic bg-white border border-stone-200 rounded-xl">
          No drafts match the filter.
        </div>
      )}
      {items.map((d) => (
        <div key={d.draft_id} className="bg-white border border-stone-200 rounded-xl p-2"
             data-testid={`aina-lib-${d.draft_id}`}>
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase font-black text-stone-500">{d.platform} · {d.language} · v{d.version}</div>
            <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full text-white uppercase ${
              d.approval_status === "approved" ? "bg-emerald-600" :
              d.approval_status === "rejected" ? "bg-rose-600" : "bg-stone-500"}`}>
              {d.approval_status}
            </span>
          </div>
          <pre className="text-[11px] whitespace-pre-wrap font-sans text-stone-800 max-h-28 overflow-auto mt-1">{d.content}</pre>
        </div>
      ))}
    </div>
  );
}

